import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { safeJson } from '../../utils/safeJson';
import { API } from '../../config/api';
import {
  Users,
  FileText,
  Music,
  Video,
  TrendingUp,
  UserPlus,
  Loader2,
  BarChart3,
  Heart,
  MessageCircle,
  RefreshCw
} from 'lucide-react';

export default function AnalyticsPanel({ getAuthHeaders }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/control-center/analytics?days=${days}`, {
        headers: getAuthHeaders(),
      });
      const result = await safeJson(res);
      if (result.ok && result.data?.users) {
        setAnalytics(result.data);
      }
    } catch (e) {
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, [days, getAuthHeaders]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <Card className="p-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  if (!analytics) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        Failed to load analytics.
        <Button size="sm" variant="outline" onClick={fetchAnalytics} className="ml-2">Retry</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Platform Analytics
        </h2>
        <div className="flex gap-2 items-center">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? 'default' : 'outline'}
              onClick={() => setDays(d)}
              data-testid={`analytics-${d}d`}
            >
              {d}d
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={fetchAnalytics} data-testid="analytics-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Total Users" value={analytics.users.total} accent="text-blue-500" />
        <MetricCard icon={UserPlus} label={`New Users (${days}d)`} value={analytics.users.new} accent="text-green-500" />
        <MetricCard icon={Heart} label="Total Likes" value={analytics.engagement.total_likes} accent="text-red-500" />
        <MetricCard icon={MessageCircle} label="Total Comments" value={analytics.engagement.total_comments} accent="text-yellow-500" />
      </div>

      {/* Content Creation Stats */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Content Created (Last {days} Days)
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <StatBar icon={FileText} label="Posts" value={analytics.content.new_posts} color="bg-blue-500" />
          <StatBar icon={Music} label="Tracks" value={analytics.content.new_tracks} color="bg-purple-500" />
          <StatBar icon={Video} label="Videos" value={analytics.content.new_videos} color="bg-pink-500" />
        </div>
      </Card>

      {/* Role Distribution */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">User Role Distribution</h3>
        <div className="space-y-2">
          {analytics.users.role_distribution.map(r => {
            const pct = analytics.users.total > 0 ? ((r.count / analytics.users.total) * 100).toFixed(1) : 0;
            return (
              <div key={r.role} className="flex items-center gap-3" data-testid={`role-dist-${r.role}`}>
                <span className="text-sm w-32 text-muted-foreground capitalize">{r.role}</span>
                <div className="flex-1 h-6 bg-muted/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary/70 rounded-full transition-all"
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  />
                </div>
                <span className="text-sm font-medium w-16 text-right">{r.count} ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Creators */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Top Creators by Followers</h3>
          <div className="space-y-3">
            {analytics.top_creators.length === 0 && (
              <p className="text-sm text-muted-foreground">No creators with followers yet.</p>
            )}
            {analytics.top_creators.map((c, i) => (
              <div key={c.user_id} className="flex items-center gap-3" data-testid={`top-creator-${i}`}>
                <span className="text-sm font-bold text-muted-foreground w-5">{i + 1}</span>
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
                  {(c.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.name || 'Unknown'}</p>
                  {c.handle && <p className="text-xs text-muted-foreground">@{c.handle}</p>}
                </div>
                <Badge variant="secondary">{c.followers_count} followers</Badge>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Signups */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Recent Signups</h3>
          <div className="space-y-3">
            {analytics.recent_signups.length === 0 && (
              <p className="text-sm text-muted-foreground">No recent signups.</p>
            )}
            {analytics.recent_signups.map((s, i) => (
              <div key={s.user_id} className="flex items-center gap-3" data-testid={`recent-signup-${i}`}>
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                  <UserPlus className="w-4 h-4 text-green-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.name || 'No name'}</p>
                  <p className="text-xs text-muted-foreground">{s.email}</p>
                </div>
                <div className="text-right">
                  <Badge variant="outline" className="text-xs capitalize">{s.role || 'listener'}</Badge>
                  {s.created_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(s.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Engagement Overview */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Platform Engagement</h3>
        <div className="grid grid-cols-3 gap-6 text-center">
          <div className="p-4 bg-muted/20 rounded-lg">
            <p className="text-3xl font-bold">{analytics.engagement.total_follows}</p>
            <p className="text-sm text-muted-foreground">Follows</p>
          </div>
          <div className="p-4 bg-muted/20 rounded-lg">
            <p className="text-3xl font-bold">{analytics.engagement.total_likes}</p>
            <p className="text-sm text-muted-foreground">Likes</p>
          </div>
          <div className="p-4 bg-muted/20 rounded-lg">
            <p className="text-3xl font-bold">{analytics.engagement.total_comments}</p>
            <p className="text-sm text-muted-foreground">Comments</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Icon className={`w-8 h-8 ${accent}`} />
        <div>
          <p className="text-2xl font-bold">{value ?? 0}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function StatBar({ icon: Icon, label, value, color }) {
  return (
    <div className="text-center p-4 bg-muted/20 rounded-lg">
      <Icon className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
      <p className="text-2xl font-bold">{value ?? 0}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
